import Konva from 'konva';

import { Bounding } from '../../core/models/bounding';
import { Element } from '../../equipment/models/elements/element';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { IPlanogram } from './planogram';
import { konvaSettings } from '../../equipment/models/konvaSettings';
import { PlanogramViewBehavior } from './planogramViewBehavior';
import { Point } from '../../core/models/point';
import { POSMaterial } from './POSMaterials/POSMaterial';
import { ProductGroup } from './product-group';
import { ShelfElement } from './elements/shelfElement';

import { Utils } from '../../core/services/utils.service';

const BACK_WALLS_LIST: Array<EquipmentElements> = [
	EquipmentElements.BackWall,
	EquipmentElements.CentralPillar,
	EquipmentElements.HookWall,
	EquipmentElements.PerforatedWall,
	EquipmentElements.Showcase
];

export class PlanogramTopBehavior extends PlanogramViewBehavior {
	private _detailedElements: Array<Element>;

	constructor(planogram: IPlanogram, detailedElement: Element, scale: number, beginPoint: Point) {
		super(planogram, detailedElement, scale, beginPoint);

		this._detailedElements = [
			detailedElement,
			...(planogram.stand as any).orderedElements.filter((el: Element) => el.depend == detailedElement),
			...planogram.stand.getVerticalIntersectedElements(detailedElement)
		];

		if (detailedElement.depend?.parent.is(...BACK_WALLS_LIST)) {
			if (detailedElement.depend.getBoundingY().max > detailedElement.getBoundingY().max) {
				this._detailedElements.push(detailedElement.depend);
			}
		}
	}

	public convertCanvasPointToPlanogram(point: Point): Point {
		const canvasXPoint: number = point.x - konvaSettings.detailedCanvasOffset - this.beginPoint.x;
		const canvasZPoint: number = point.y - konvaSettings.detailedCanvasOffset - this.beginPoint.y;

		return {
			x: Utils.round(this.getOffset().x + canvasXPoint / this.scale),
			y: this.element.getBoundingY().max,
			z: Utils.round(this.getOffset().y + canvasZPoint / this.scale)
		};
	}

	public convertPlanogramPointToCanvas(point: Point): Point {
		return {
			x: Utils.round(point.x * this.scale + konvaSettings.detailedCanvasOffset + this.beginPoint.x),
			y: Utils.round(point.y * this.scale + konvaSettings.detailedCanvasOffset + this.beginPoint.y),
		};
	}

	public getActiveProductGroups(activeProducts: Array<ProductGroup>): Array<Array<ProductGroup>> {
		const activeProductGroups: Array<Array<ProductGroup>> = Utils.groupBy<ProductGroup>(
			activeProducts.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x),
			'element'
		)
			.reduce((result: Array<Array<ProductGroup>>, group: Array<ProductGroup>) => {
				return [...result, ...Utils.groupByChildProperty(group, 'begin', 'x')];
			}, [])
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);

		activeProductGroups.forEach((group: Array<ProductGroup>) => {
			group.sort((a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z || a.begin.y - b.begin.y);
		});

		return activeProductGroups;
	}

	public getAnchorElement(product: ProductGroup, point: Point): Element {
		const boundingX: Bounding = this.element.getBoundingX();
		const boundingZ: Bounding = this.element.getAbsoluteBoundingZ();

		if (point.x > boundingX.max || point.x < boundingX.min || point.z > boundingZ.max || point.z < boundingZ.min) return null;

		let element: Element = (this.element as ShelfElement).getPointedElement(
			point.x,
			point.z,
			this.planogram.stand
		);

		return element;
	}

	public getCorrectZCoordinate(element: Element, z: number, product: ProductGroup): number {
		return z;
	}

	public getOffset(): Point {
		const elementProducts: Array<ProductGroup> = this.element.products.flat();
		return {
			x: Math.min(
				...this._detailedElements.map((el: Element) => el.getBoundingX().min),
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingX().min)
			),
			y: Math.min(
				...this._detailedElements.map((el: Element) => el.getBoundingZ().min),
				...elementProducts.map((pg: ProductGroup) => pg.getBoundingZ().min)
			)
		};
	}

	public getPoint(product: ProductGroup, canvasElement: Konva.Group): Point {
		const transform: Konva.Transform = canvasElement.getAbsoluteTransform();
		const rotatedBeginPoint: Point = transform.point({ x: 0, y: canvasElement.height() });

		const canvasXPoint: number = rotatedBeginPoint.x - konvaSettings.detailedCanvasOffset - this.beginPoint.x;
		const canvasZPoint: number = rotatedBeginPoint.y - konvaSettings.detailedCanvasOffset - this.beginPoint.y;

		return {
			x: Utils.round(this.getOffset().x + canvasXPoint / this.scale),
			y: product.element.anchorYCoordinate,
			z: Utils.round(this.getOffset().y + canvasZPoint / this.scale)
		};
	}

	public getProductAngle(element: Element, point: Point): number {
		const baseWithCentralPillar: boolean = element.relatedElements
			.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar));

		if (element.depend?.parent.is(EquipmentElements.CentralPillar) || baseWithCentralPillar) {
			const bounding: Bounding = element.getBoundingZ();
			const center: number = Utils.round((bounding.max + bounding.min) / 2);

			if (bounding.min < point.z && center > point.z) {
				return 180;
			}
		}

		return 0;
	}

	public moveProducts(productGroups: Array<ProductGroup>, horizontal: number, vertical: number): boolean {
		if ((!horizontal && !vertical) || !productGroups.length) return false;
		const allCanRotate: boolean = productGroups.every((pg: ProductGroup) => pg.element.canRotateProductGroup(pg));
		if (vertical && !this.planogram.stand.arbitraryLayout && !allCanRotate) return false;

		let movementPerformed: boolean = false;
		productGroups.forEach((pg: ProductGroup) => {
			if (pg.canMove(horizontal, 0, vertical)) {
				pg.move(horizontal, 0, vertical);
				movementPerformed = true;
			}
		});

		return movementPerformed;
	}

	public movePOSMaterials(materials: Array<POSMaterial>, horizontal: number, vertical: number): boolean {
		if ((!horizontal && !vertical) || !materials.length) return false;

		let movementPerformed: boolean = false;
		materials.forEach((m: POSMaterial) => {
			m.x = Utils.round(m.x + horizontal);
			m.y = Utils.round(m.y + vertical);
			movementPerformed = true;
		});

		return movementPerformed;
	}
}